import { legacyDeserializerHandle } from './legacy';
import { deepEquals, fixNonSelfClosingIframes } from './utils/common-utils';
import { componentsSerializer, componentsDeserializer } from './components-map';
import type { ArticleComponent, ArticleComponentType, DomParser, DomNode } from './types';

export interface ArticleTemplate {
	id: string;
	index: number;
}

export type DeserializedComponent = ArticleComponent & { template?: ArticleTemplate };

const isRunningInBrowser = (): boolean =>
	typeof window !== 'undefined' && !!window.document;

export const deserialize = (html: string, locale: string, parser: DomParser): DeserializedComponent[] => {
	const inputHtml = isRunningInBrowser() ? html : fixNonSelfClosingIframes(html);
	return deserializerHandle(parser)(inputHtml, locale);
};

export const deserializerHandle = ($: DomParser) => (html: string, locale: string): DeserializedComponent[] =>
	isValidHtmlContent($)(html, locale)
		? deserializeHtmlToComponents($)(html, locale)
		: deserializeLegacyHtmlToComponents($)(html, locale);

export const serialize = (components: ArticleComponent[]): string =>
	serializeComponentsToHtml(components as DeserializedComponent[]);

export const isValidHtmlContent = ($: DomParser) => (html: string, locale: string): boolean => {
	try {
		const parsedComponents = deserializeHtmlToComponents($)(html, locale);
		const reversedHtml = serializeComponentsToHtml(parsedComponents);
		const doubleParsedComponents = deserializeHtmlToComponents($)(reversedHtml, locale);

		const $children = $('<div>').html(html).children();
		const componentCount = $children.filter('div[data-component-type]').length;
		const childrenCount = $children.length;

		const childrenCountMatch = childrenCount === parsedComponents.length;
		const componentCountMatch = componentCount === parsedComponents.length;
		const hasComponents = componentCount > 0;
		const componentContentMatch = deepEquals(parsedComponents, doubleParsedComponents);

		return hasComponents && childrenCountMatch && componentCountMatch && componentContentMatch;
	} catch {
		return false;
	}
};

export const serializeComponentsToHtml = (components: DeserializedComponent[]): string =>
	components
		.map((component) => {
			const innerValue = (componentsSerializer as Record<string, (v: unknown) => string>)[component.type](
				component.value
			);
			return `<div data-component-type="${component.type}">${innerValue}</div>`;
		})
		.join('');

export const deserializeHtmlToComponents = ($: DomParser) =>
	(html: string, locale: string): DeserializedComponent[] =>
		$(html)
			.filter('div[data-component-type]')
			.toArray()
			.map((node: unknown): DeserializedComponent => {
				const $node = $(node);
				const templateId: string | undefined = $node.attr('data-template-id');
				const templatePartIndexString: string | undefined = $node.attr('data-template-part-index');
				const type = $node.attr('data-component-type') as ArticleComponentType;
				const value = (componentsDeserializer($, locale) as Record<string, (n: DomNode) => unknown>)[type](
					$node.contents()
				);
				const componentData = { type, value } as DeserializedComponent;

				if (templateId) {
					componentData.template = {
						id: templateId,
						index: parseInt(templatePartIndexString as string, 10),
					};
				}
				return componentData;
			});

export const deserializeLegacyHtmlToComponents = ($: DomParser) =>
	(legacyHtml: string, locale: string): DeserializedComponent[] =>
		legacyDeserializerHandle($)(legacyHtml, locale) as DeserializedComponent[];

export { componentsSerializer, componentsDeserializer } from './components-map';
export type {
	ArticleComponent,
	ArticleComponentType,
	ArticleComponentWithKey,
	BaseArticleComponent,
	ComponentTypeToValueMap,
	CodeComponentValue,
	CollapsibleComponentValue,
	DomNode,
	DomParser,
	HeadingComponentValue,
	HtmlComponentValue,
	IframeComponentValue,
	ImageComponentValue,
	InformativeComponentValue,
	LineComponentValue,
	LinkableImage,
	MarkdownComponentValue,
	QuestionComponentValue,
	StepByStepComponentStep,
	StepByStepComponentValue,
	TableComponentValue,
	TabsComponentValue,
	TextComponentValue,
	VideoComponentValue,
} from './types';
